import { ComponentProps } from 'react';
import { z } from 'zod';
import { MultiValue, SingleValue } from 'chakra-react-select';
import {
    Stack, Text,
} from '@chakra-ui/react';
import { CreatableSelect } from '../creatable-select/creatable-select';

type EmailOption = {
    group?: string;
    label: string;
    value: string;
};

type GroupedEmailOption = {
    group: string;
    options: EmailOption[];
};

export function EmailSelect({
    options,
    colorScheme = 'secondary',
    placeholder = 'Select email addresses',
    isInvalid,
    onChange,
    isLoading,
    isCreatable = true,
    isMulti = true,
}: {
    isCreatable?: boolean;
    isMulti?: boolean;
    options: EmailOption[];
    isLoading: boolean;
    colorScheme?: ComponentProps<typeof CreatableSelect>['colorScheme'];
    placeholder?: string;
    isInvalid?: ComponentProps<typeof CreatableSelect>['isInvalid'];
    onChange?: (value: MultiValue<EmailOption> | SingleValue<EmailOption>) => void;
}) {
    const groupOptions = (emailOptions: EmailOption[]): GroupedEmailOption[] => {
        let groups = [] as string[];

        emailOptions.forEach((option) => {
            if (option.group !== undefined && !groups.includes(option.group)) {
                groups.push(option.group);
            }
        });

        return groups.map((group) => ({
            group,
            options: emailOptions.filter((option) => option.group === group),
        }));
    };

    const groupedOptions = groupOptions(options);

    const formatGroupLabel = (data: GroupedEmailOption) => (
        <Text color="charcoal" fontSize="sm">{data.group}</Text>
    );

    const formatOptionLabel = (option: EmailOption) => (
        <Stack
            direction={['column', 'row']}
            justifyContent="space-between"
            w="full"
            alignItems={['flex-start', 'center']}
            gap={[0, 1]}
            py={1}
        >
            <Text color="charcoal.500" fontSize="sm">{option.value}</Text>
            <Text textAlign="right" color="muted.500" fontSize="xs">{option.label}</Text>
        </Stack>
    );

    return (
        <CreatableSelect
            isMulti={isMulti}
            isClearable
            isInvalid={isInvalid}
            isLoading={isLoading}
            options={groupedOptions}
            colorScheme={colorScheme}
            placeholder={placeholder}
            formatGroupLabel={formatGroupLabel}
            onChange={onChange}
            formatOptionLabel={formatOptionLabel}
            formatCreateLabel={() => 'Add new email'}
            getNewOptionData={(value) => ({
                label: '',
                value,
            })}
            isValidNewOption={(inputValue) => isCreatable
                && z.string().email().safeParse(inputValue).success}
        />
    );
}

export { EmailOption };
